/**
 * @file
 * Sidebar menu functionality.
 */

import { loadOnReady } from "../../../../.storybook/behaviors";

const menu_wrapper = '.sidebar-menu__wrapper';
const escapeAttributeValue = (value) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const getMenuWrapper = (el) => el?.closest(menu_wrapper) || null;
const getScopedElementById = (id, scope) => {
  if (!id || !scope) {
    return null;
  }

  return scope.querySelector(`[id="${escapeAttributeValue(id)}"]`);
};

// Full sidebar menu behavior function.
Drupal.sidebarMenuToggle = {
  updateToggleLabel: (toggle, isExpanded) => {
    // Read the server-translated labels from the markup so the accessible name
    // stays correct in any language (and works outside Drupal, e.g. Storybook,
    // where Drupal.t is not available).
    const openLabel = toggle.dataset.openLabel || 'Open section navigation';
    const closeLabel = toggle.dataset.closeLabel || 'Close section navigation';
    const label = isExpanded ? closeLabel : openLabel;
    const text = toggle.querySelector('.visually-hidden');

    toggle.setAttribute('aria-label', label);
    if (text) {
      text.textContent = label;
    }
  },

  syncWrapperOpenState: (menuWrapper, isOpen) => {
    if (menuWrapper) {
      menuWrapper.classList.toggle('is-open', isOpen);
    }
  },

  init: function (context) {
    once('sidebarMenuToggle', menu_wrapper, context).forEach((menuWrapper) => {
      const toggle = menuWrapper.querySelector('.sidebar-menu__menu-toggle');
      if (!toggle) {
        return;
      }

      const menuId = toggle.getAttribute('aria-controls');
      // The level-1 list has no id in the markup; resolve it directly and
      // stamp the toggle's aria-controls id onto it so the two stay linked.
      const menu =
        (menuId && getScopedElementById(menuId, menuWrapper)) ||
        menuWrapper.querySelector('.sidebar-menu--level-1');

      if (menu && menuId && menu.id !== menuId) {
        menu.id = menuId;
      }

      const handleToggle = (e) => {
        e.preventDefault();
        this.toggleMenu(toggle, menu, menuWrapper);
      };

      // A single click handler covers pointer, touch and keyboard
      // activation; binding touchend as well risks a double toggle on touch.
      toggle.addEventListener('click', (e) => {
        handleToggle(e);
      });

      menuWrapper.addEventListener('keyup', (e) => {
        if (
          (e.key === 'Escape' || e.key === 'Esc') &&
          toggle.getAttribute('aria-expanded') === 'true'
        ) {
          this.closeMenu(toggle, menu, menuWrapper);
          toggle.focus();
        }
      });

      // Set initial open/closed state based on viewport.
      this.setInitialState(toggle, menu, menuWrapper);

      // Re-evaluate state when crossing the mobile/desktop breakpoint.
      const mql = window.matchMedia('(width >= 1024px)');
      mql.addEventListener('change', () => {
        this.setInitialState(toggle, menu, menuWrapper);
      });
    });
  },

  // Set initial menu state: always open on desktop; on mobile, open only
  // when the current page is part of the menu's active trail.
  setInitialState: (toggle, menu, menuWrapper) => {
    const isMobile = window.matchMedia('(width <= 1024px)').matches;
    const hasActiveTrail = menu
      ? menu.querySelector('.menu__link--active-trail') !== null
      : false;

    if (!isMobile || hasActiveTrail) {
      Drupal.sidebarMenuToggle.openMenu(toggle, menu, menuWrapper);
    } else {
      Drupal.sidebarMenuToggle.closeMenu(toggle, menu, menuWrapper);
    }
  },

  openMenu: (toggle, menu, menuWrapper = null) => {
    toggle.setAttribute('aria-expanded', 'true');
    Drupal.sidebarMenuToggle.updateToggleLabel(toggle, true);
    if (menu) {
      menu.setAttribute('aria-hidden', 'false');
    }
    Drupal.sidebarMenuToggle.syncWrapperOpenState(menuWrapper || getMenuWrapper(toggle), true);
  },

  closeMenu: (toggle, menu, menuWrapper = null) => {
    toggle.setAttribute('aria-expanded', 'false');
    Drupal.sidebarMenuToggle.updateToggleLabel(toggle, false);
    if (menu) {
      menu.setAttribute('aria-hidden', 'true');
    }

    // Close all open nested menu and update aria attributes accordingly.
    const wrapper = menuWrapper || getMenuWrapper(toggle);
    Drupal.sidebarSubmenuToggle.closeAllMenus(wrapper);
    Drupal.sidebarMenuToggle.syncWrapperOpenState(wrapper, false);
  },

  toggleMenu: (toggle, menu, menuWrapper) => {
    if (toggle.getAttribute('aria-expanded') === 'true') {
      Drupal.sidebarMenuToggle.closeMenu(toggle, menu, menuWrapper);
    } else {
      Drupal.sidebarMenuToggle.openMenu(toggle, menu, menuWrapper);
    }
  },
};

// Submenus behavior function.
Drupal.sidebarSubmenuToggle = {
  updateToggleLabel: (el, isExpanded) => {
    // Use the full, server-translated labels supplied in the markup so the
    // accessible name stays correct in any language (no string parsing).
    const label = isExpanded
      ? el.getAttribute('data-label-collapse')
      : el.getAttribute('data-label-expand');
    if (!label) {
      return;
    }

    el.setAttribute('aria-label', label);
    const text = el.querySelector('.visually-hidden');
    if (text) {
      text.textContent = label;
    }
  },

  getControlledSubmenu: (el, menuWrapper = null) => {
    const ariaControls = el.getAttribute('aria-controls');
    const scope = menuWrapper || getMenuWrapper(el);
    return getScopedElementById(ariaControls, scope);
  },

  getToggleByControlsId: (menuWrapper, controlsId) => {
    if (!menuWrapper || !controlsId) {
      return null;
    }

    return (
      Array.from(menuWrapper.querySelectorAll('.sidebar-menu__submenu-toggle')).find(
        (toggle) => toggle.getAttribute('aria-controls') === controlsId
      ) || null
    );
  },

  init: function (context) {
    once('sidebarSubmenuToggleWrapper', menu_wrapper, context).forEach((menuWrapper) => {
      menuWrapper.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          const toggle = this.getActiveSubMenuToggle(menuWrapper);

          if (toggle) {
            this.closeSubMenu(toggle, menuWrapper);
            toggle.focus();
            return;
          }

          const sidebarToggle = menuWrapper.querySelector('.sidebar-menu__menu-toggle');
          const sidebarMenu = menuWrapper.querySelector('.sidebar-menu--level-1');
          if (sidebarToggle?.getAttribute('aria-expanded') === 'true') {
            Drupal.sidebarMenuToggle.closeMenu(sidebarToggle, sidebarMenu, menuWrapper);
            sidebarToggle.focus();
          }
        }
      });

      this.openActiveSubMenus(menuWrapper);
      this.initializeSubmenuAriaHidden(menuWrapper);
    });

    // Menu toggle — use once() to prevent duplicate handlers on re-attach.
    once('sidebarSubmenuToggleButton', '.sidebar-menu__submenu-toggle', context).forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSubMenu(el);
      });
    });
  },

  openSubMenu: (el, menuWrapper = null) => {
    const menu = Drupal.sidebarSubmenuToggle.getControlledSubmenu(el, menuWrapper);
    if (!menu) {
      return;
    }

    el.setAttribute('aria-expanded', 'true');
    Drupal.sidebarSubmenuToggle.updateToggleLabel(el, true);
    el.classList.add('submenu-is-open');
    menu.classList.add('submenu-is-open');
    menu.setAttribute('aria-hidden', 'false');
  },

  closeSubMenu: (el, menuWrapper = null) => {
    const menu = Drupal.sidebarSubmenuToggle.getControlledSubmenu(el, menuWrapper);
    if (!menu) {
      return;
    }

    el.setAttribute('aria-expanded', 'false');
    Drupal.sidebarSubmenuToggle.updateToggleLabel(el, false);
    el.classList.remove('submenu-is-open');
    menu.classList.remove('submenu-is-open');
    menu.setAttribute('aria-hidden', 'true');
  },

  // Initialize aria-hidden on submenus based on current expanded state.
  initializeSubmenuAriaHidden: (menuWrapper) => {
    menuWrapper.querySelectorAll('.sidebar-menu__submenu-toggle').forEach((toggle) => {
      const menu = Drupal.sidebarSubmenuToggle.getControlledSubmenu(toggle, menuWrapper);
      if (!menu) {
        return;
      }

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      menu.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
      Drupal.sidebarSubmenuToggle.updateToggleLabel(toggle, isExpanded);
    });
  },

  toggleSubMenu: (el) => {
    const menuWrapper = getMenuWrapper(el);
    if (!menuWrapper) {
      return;
    }

    if (el.getAttribute('aria-expanded') === 'true') {
      Drupal.sidebarSubmenuToggle.closeSubMenu(el, menuWrapper);
    } else {
      Drupal.sidebarSubmenuToggle.openSubMenu(el, menuWrapper);
    }
  },

  // Find the currently relevant submenu toggle based on focus.
  getActiveSubMenuToggle: (menuWrapper) => {
    const activeElement = document.activeElement;
    if (!activeElement || !menuWrapper.contains(activeElement)) {
      return null;
    }

    if (activeElement.classList?.contains('sidebar-menu__submenu-toggle')) {
      return activeElement;
    }

    const parentMenu = activeElement.closest('.sidebar-menu__items');
    if (!parentMenu?.id) {
      return null;
    }

    return Drupal.sidebarSubmenuToggle.getToggleByControlsId(menuWrapper, parentMenu.id);
  },

  // On page load, open any submenus that contain the active page.
  openActiveSubMenus: (menuWrapper) => {
    let anyOpened = false;

    menuWrapper.querySelectorAll('.is-active-menu-parent').forEach((menu) => {
      const toggle = Drupal.sidebarSubmenuToggle.getToggleByControlsId(menuWrapper, menu.id);
      if (toggle) {
        Drupal.sidebarSubmenuToggle.openSubMenu(toggle, menuWrapper);
        anyOpened = true;
      }
    });

    // If any submenus are open, ensure the sidebar itself is also open so
    // the toggle reflects the correct icon, aria state, and CSS class.
    if (anyOpened) {
      const sidebarToggle = menuWrapper.querySelector('.sidebar-menu__menu-toggle');
      if (sidebarToggle) {
        const menuId = sidebarToggle.getAttribute('aria-controls');
        const menu =
          (menuId && getScopedElementById(menuId, menuWrapper)) ||
          menuWrapper.querySelector('.sidebar-menu--level-1');
        Drupal.sidebarMenuToggle.openMenu(sidebarToggle, menu, menuWrapper);
      }
    }
  },

  closeAllMenus: (menuWrapper = null) => {
    const scope = menuWrapper || document;
    const subMenuToggle = scope.querySelectorAll('.sidebar-menu__submenu-toggle');

    subMenuToggle.forEach((el) => {
      if (el.getAttribute('aria-expanded') === 'true') {
        Drupal.sidebarSubmenuToggle.closeSubMenu(el, menuWrapper || getMenuWrapper(el));
      }
    });
  },
};

// Register the behaviors. loadOnReady wires these as Drupal.behaviors in
// Drupal and runs them via attachBehaviors in Storybook, so the menu behaves
// identically in both environments.
loadOnReady((context) => {
  Drupal.sidebarMenuToggle.init(context);
}, 'sidebarMenuToggle');

loadOnReady((context) => {
  Drupal.sidebarSubmenuToggle.init(context);
}, 'sidebarSubmenuToggle');
